import { ReactElement, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Button from "../../components/staff/Button";
import CheckBox from "../../components/staff/CheckBox";
import InputText from "../../components/staff/InputText";
import Plus from "../../components/Atomos/Icons/EditMode/Plus";
import CloseModal from "../../components/Atomos/Icons/EditMode/CloseModal";
import SaveChanges from "../../components/Atomos/Icons/EditMode/SaveChanges";
import Trash from "../../components/Atomos/Icons/EditMode/Trash";
import { Cancel } from "../../components/Atomos/Icons/Cancel";
import {
  getContactTopics,
  saveContactTopic,
  type ContactTopic,
} from "../../Services/Person";
import { toastError, toastSuccess } from "../../utils/toast";

function topicError(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  if (data && typeof data === "object" && "message" in data) {
    const message = String((data as { message?: unknown }).message ?? "").trim();
    if (message) {
      return message;
    }
  }
  return "The option could not be saved.";
}

function sameText(left: string, right: string): boolean {
  return left.localeCompare(right, undefined, { sensitivity: "accent" }) === 0;
}

const buttonIcon = (icon: ReactElement) => (
  <span className="block h-5 w-5 overflow-hidden text-current [&_svg]:h-5 [&_svg]:w-5 [&_svg]:fill-current">
    {icon}
  </span>
);

const modalButtonClass =
  "!h-auto !min-h-0 !w-[140px] !shrink-0 !justify-center !gap-2 !rounded !border !border-solid !py-[10px] !pl-5 !pr-4 !leading-5 !outline-none";

export default function ContactTopicCatalog(): ReactElement {
  const [topics, setTopics] = useState<ContactTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getContactTopics()
      .then((data) => {
        if (!cancelled) {
          setTopics(data.topics ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const closeModal = () => {
    if (busy) {
      return;
    }
    setModalOpen(false);
    setEditingId(null);
    setDraft("");
    setDraftError("");
  };

  useEffect(() => {
    if (!modalOpen) {
      return;
    }
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [modalOpen, busy]);

  const openCreate = () => {
    setEditingId(null);
    setDraft("");
    setDraftError("");
    setModalOpen(true);
  };

  const openEdit = (topic: ContactTopic) => {
    setEditingId(topic.id);
    setDraft(topic.text);
    setDraftError("");
    setModalOpen(true);
  };

  const saveTopic = async () => {
    const text = draft.trim();
    if (!text) {
      setDraftError("Required");
      return;
    }
    if (topics.some((topic) => topic.id !== editingId && sameText(topic.text, text))) {
      setDraftError("This option already exists");
      return;
    }
    setBusy(true);
    try {
      const data = await saveContactTopic(
        editingId
          ? { action: "update", id: editingId, text }
          : { action: "create", text },
      );
      setTopics(data.topics ?? []);
      setModalOpen(false);
      setEditingId(null);
      setDraft("");
      setDraftError("");
      toastSuccess("Option saved.");
    } catch (error) {
      setDraftError(topicError(error));
    } finally {
      setBusy(false);
    }
  };

  const deleteTopic = async () => {
    if (!editingId) {
      return;
    }
    setBusy(true);
    try {
      const data = await saveContactTopic({ action: "delete", id: editingId });
      setTopics(data.topics ?? []);
      setModalOpen(false);
      setEditingId(null);
      setDraft("");
      setDraftError("");
      toastSuccess("Option deleted.");
    } catch (error) {
      toastError(topicError(error));
    } finally {
      setBusy(false);
    }
  };

  const modal = modalOpen
    ? createPortal(
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
          onClick={closeModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-topic-modal-title"
            className="w-full max-w-3xl rounded-lg bg-white p-6 shadow-xl md:p-8"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <h3
                id="contact-topic-modal-title"
                className="m-0 font-roboto text-xl font-bold leading-normal text-Default"
              >
                {editingId
                  ? "Comments & Questions - Edit Topic"
                  : "Comments & Questions - Add New Topic"}
              </h3>
              <button
                type="button"
                className="flex h-6 w-6 shrink-0 items-center justify-center text-gray-font"
                aria-label="Close"
                disabled={busy}
                onClick={closeModal}
              >
                <CloseModal />
              </button>
            </div>

            <div className="mt-6">
              <InputText
                title="New Topic"
                name="contact-topic"
                value={draft}
                placeholder="Add Topic"
                error={Boolean(draftError)}
                errorMessage={draftError}
                disabled={busy}
                onChange={(value) => {
                  setDraft(value);
                  setDraftError("");
                }}
              />
            </div>

            <div className="mt-8 flex items-center justify-end gap-3">
              {editingId ? (
                <Button
                  text="Delete"
                  color="danger"
                  variant="filled"
                  size="fixed"
                  icon={buttonIcon(<Trash />)}
                  iconPosition="right"
                  disabled={busy}
                  className={`${modalButtonClass} !mr-auto !border-red-600 !bg-red-600 !text-white hover:!border-red-700 hover:!bg-red-700`}
                  onClick={() => {
                    void deleteTopic();
                  }}
                />
              ) : null}
              <Button
                text="Cancel"
                color="neutral"
                variant="outlined"
                size="fixed"
                icon={buttonIcon(<Cancel />)}
                iconPosition="right"
                disabled={busy}
                className={`${modalButtonClass} !border-gray-font !bg-white !text-gray-font hover:!border-neutral-dark hover:!bg-neutral-dark hover:!text-white`}
                onClick={closeModal}
              />
              <Button
                text="Save"
                color="secondary"
                variant="filled"
                size="fixed"
                icon={buttonIcon(<SaveChanges />)}
                iconPosition="right"
                disabled={busy}
                className={`${modalButtonClass} !border-Default !bg-Default !text-white hover:!border-accent-Default-dark hover:!bg-accent-Default-dark`}
                onClick={() => {
                  void saveTopic();
                }}
              />
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <section className="flex w-full flex-col items-start gap-6 self-stretch bg-white p-6">
      <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="m-0 font-roboto text-2xl font-bold not-italic leading-normal text-Default">
          Comments & Questions
        </h2>
        <Button
          text="Add New Topic"
          color="secondary"
          variant="filled"
          size="fixed"
          icon={buttonIcon(<Plus />)}
          iconPosition="right"
          disabled={loading || failed}
          onClick={openCreate}
        />
      </div>

      {loading ? <p className="m-0 text-sm text-gray-font">Loading options...</p> : null}
      {failed ? <p className="m-0 text-sm text-gray-font">Options could not be loaded.</p> : null}
      {!loading && !failed && topics.length === 0 ? (
        <p className="m-0 text-sm text-gray-font">No options are in the selector yet.</p>
      ) : null}

      {!loading && !failed && topics.length > 0 ? (
        <div className="flex w-full flex-wrap items-center gap-x-10 gap-y-4">
          {topics.map((topic) => (
            <CheckBox
              key={topic.id}
              label={topic.text}
              checked={false}
              tooltip="Edit topic"
              labelClassName="font-roboto text-base font-normal leading-[130%]"
              onCheckedChange={() => openEdit(topic)}
            />
          ))}
        </div>
      ) : null}
      {modal}
    </section>
  );
}
